const API_BASE = "/api";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);
let csrfInitPromise = null;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return "";
}

async function ensureCsrfCookie() {
  if (!csrfInitPromise) {
    csrfInitPromise = fetch(`${API_BASE}/auth/csrf/`, {
      method: "GET",
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed CSRF init: HTTP ${response.status}`);
        }
        return response;
      })
      .catch((error) => {
        csrfInitPromise = null;
        throw error;
      });
  }
  return csrfInitPromise;
}

async function requestJson(path, { method = "GET", body } = {}) {
  const normalizedMethod = method.toUpperCase();
  const options = {
    method: normalizedMethod,
    credentials: "same-origin",
    headers: {},
  };

  if (!SAFE_METHODS.has(normalizedMethod)) {
    await ensureCsrfCookie();
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }

  if (typeof body !== "undefined") {
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

function rpcGet(method) {
  return requestJson(`/rpc/${method}/`);
}

function rpcPost(method, params = {}) {
  return requestJson(`/rpc/${method}/`, {
    method: "POST",
    body: { params },
  });
}

export async function authCsrf() {
  return requestJson("/auth/csrf/");
}

export async function login({ username, password }) {
  return requestJson("/auth/login/", {
    method: "POST",
    body: { username, password },
  });
}

export async function logout() {
  return requestJson("/auth/logout/", {
    method: "POST",
  });
}

export async function requestAllBlurbs() {
  return rpcGet("request_all_blurbs");
}

export async function requestBlurb(name) {
  return rpcPost("request_blurb", { name });
}

export async function saveBlurb({ name, value }) {
  return rpcPost("save_blurb", { name, value });
}

export async function requestAuthInfo() {
  return rpcGet("request_auth_info");
}

export async function requestGlobalConfig() {
  return rpcGet("request_global_config");
}

export async function getRegistrationData(params) {
  return rpcPost("get_registration_data", params);
}

export async function getRegistrationInfo(uuid) {
  return rpcPost("get_registration_info", { uuid });
}

export async function cancelRegistrations(params) {
  return rpcPost("cancel_registrations", params);
}

export async function mailRegistrations(params) {
  return rpcPost("mail_registrations", params);
}

export async function uncancelRegistrations(params) {
  return rpcPost("uncancel_registrations", params);
}

export async function unscheduleRegistrations(params) {
  return rpcPost("unschedule_registrations", params);
}

export async function rpcLogout() {
  return rpcPost("logout");
}

export async function getTeamData() {
  return rpcGet("get_team_data");
}

export async function updateTeam(teamData) {
  return rpcPost("update_team", { team_data: teamData });
}

export async function addTeam(name) {
  return rpcPost("add_team", { name });
}

export async function autoSchedule(params) {
  return rpcPost("auto_schedule", params);
}

export async function setDateTime(params) {
  return rpcPost("set_datetime", params);
}

export { requestJson };
